"use client";

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  computeTargetFitScores,
  computeZScores,
  isSameMarketCapClass,
  isWithinMarketCapBracket,
  rankPeersByMarketCapProximity,
  type MetricPolarity,
} from "@/lib/peer-group/engine";
import { computeMetricZScores, computeRadarProfiles, RADAR_AXES, type RadarPeerInput } from "@/lib/peer-group/radar";
import { PEER_GROUP, TARGET_FIT_PRESETS } from "@/lib/config";
import { median } from "@/lib/stats";
import { QualityValueMatrix, type MatrixPoint } from "@/components/peer-group/quality-value-matrix";
import { PeerAxisBars, type AxisComparisonRow } from "@/components/peer-group/peer-axis-bars";
import type { Fundamentals, HistoricalSeries } from "@/lib/data/types";

export interface InstitutionalMember {
  symbol: string;
  name: string;
  fundamentals: Fundamentals;
  historical: HistoricalSeries;
  marketCap?: number;
}

type TargetFitKey = keyof typeof TARGET_FIT_PRESETS;

/**
 * Institutionelle Peer-Group-Auswertung (siehe backlog.md -> "Institutionelle
 * Peer-Group-Engine / Relative-Value"). Wertet die im Peer-Vergleich bereits manuell
 * zusammengestellte Aktien-Gruppe quantitativ aus (Z-Scores, Quality/Value-Matrix,
 * Achsen-Balkenvergleich, Target-Fit-Ranking) statt selbst einen Markt-Screener zu
 * betreiben - siehe docs/features/peer-group-engine.md fuer die Architektur-
 * Begruendung. Bewusst im bestehenden hellen App-Design statt einer eigenen dunklen
 * Optik, auf Eriks Wunsch konsistent mit dem restlichen Benchmark-Tab.
 */
export function InstitutionalPanel({
  own,
  peers,
}: {
  own: InstitutionalMember;
  peers: InstitutionalMember[];
}) {
  const [preset, setPreset] = useState<TargetFitKey>("quality");

  const qualifyingPeers = useMemo(() => {
    if (!own.marketCap) return [];
    const withinBracket = peers.filter(
      (p) =>
        p.marketCap !== undefined &&
        isWithinMarketCapBracket(own.marketCap!, p.marketCap, {
          minRatio: PEER_GROUP.minMarketCapRatio,
          maxRatio: PEER_GROUP.maxMarketCapRatio,
        }) &&
        // Zusaetzlich zur Ratio-Klammer: gleiche absolute Groessenklasse (backlog.md ->
        // "Marktkapitalisierungsklasse verfeinern").
        isSameMarketCapClass(own.marketCap!, p.marketCap, PEER_GROUP.marketCapClassThresholds),
    );
    return rankPeersByMarketCapProximity(own.marketCap, withinBracket, PEER_GROUP.maxPeers);
  }, [own, peers]);

  const group = useMemo(() => [own, ...qualifyingPeers], [own, qualifyingPeers]);

  const radarInputs: RadarPeerInput[] = useMemo(
    () =>
      group.map((m) => ({
        symbol: m.symbol,
        fundamentals: m.fundamentals,
        historical: m.historical,
        marketCap: m.marketCap,
      })),
    [group],
  );

  const metricZScores = useMemo(() => computeMetricZScores(radarInputs), [radarInputs]);
  const radarProfiles = useMemo(() => computeRadarProfiles(radarInputs), [radarInputs]);

  const axisRows: AxisComparisonRow[] = useMemo(() => {
    const ownProfile = radarProfiles.find((p) => p.symbol === own.symbol);
    const peerProfiles = radarProfiles.filter((p) => p.symbol !== own.symbol);
    return RADAR_AXES.map((axis) => ({
      axis,
      target: ownProfile?.scores[axis],
      peerMedian: median(
        peerProfiles.map((p) => p.scores[axis]).filter((v): v is number => v !== undefined),
      ),
    })).filter((row) => row.target !== undefined || row.peerMedian !== undefined);
  }, [radarProfiles, own.symbol]);

  const matrixPoints: MatrixPoint[] = useMemo(() => {
    const valueKey: "evToEbitda" | "pe" = own.fundamentals.evToEbitda !== undefined ? "evToEbitda" : "pe";
    const qualityKey: "roic" | "netMargin" = own.fundamentals.roic !== undefined ? "roic" : "netMargin";
    const valuePolarity: MetricPolarity = "lower-is-better";
    const qualityPolarity: MetricPolarity = "higher-is-better";

    const valueZ = computeZScores(
      group.map((m) => ({ symbol: m.symbol, value: m.fundamentals[valueKey] })),
      valuePolarity,
    );
    const qualityZ = computeZScores(
      group.map((m) => ({ symbol: m.symbol, value: m.fundamentals[qualityKey] })),
      qualityPolarity,
    );

    return group
      .map((m) => {
        const x = valueZ.find((z) => z.symbol === m.symbol)?.zScore;
        const y = qualityZ.find((z) => z.symbol === m.symbol)?.zScore;
        if (x === undefined || y === undefined) return null;
        return {
          symbol: m.symbol,
          name: m.name,
          x,
          y,
          rawValueMetric: m.fundamentals[valueKey],
          rawQualityMetric: m.fundamentals[qualityKey],
          isOwn: m.symbol === own.symbol,
        };
      })
      .filter((p): p is MatrixPoint => p !== null);
  }, [group, own.fundamentals, own.symbol]);

  const targetFitScores = useMemo(
    () => computeTargetFitScores(metricZScores, TARGET_FIT_PRESETS[preset].weights),
    [metricZScores, preset],
  );
  const winner = useMemo(
    () => [...targetFitScores].sort((a, b) => b.score - a.score)[0],
    [targetFitScores],
  );
  const winnerMember = group.find((m) => m.symbol === winner?.symbol);

  if (!own.marketCap || qualifyingPeers.length < 2) {
    return (
      <p className="rounded-lg border border-hairline bg-muted/30 p-4 text-xs text-muted-foreground">
        Für die institutionelle Peer-Group-Analyse mindestens 2 weitere Aktien mit
        ähnlicher Marktkapitalisierung hinzufügen (max. 75 % kleiner bis 1000 % größer
        als {own.symbol}).
      </p>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col gap-4"
    >
      <div>
        <h3 className="font-serif text-lg font-semibold text-ink">Institutioneller Vergleich</h3>
        <p className="text-xs text-muted-foreground">
          Quantitative Auswertung von {own.symbol} gegen {qualifyingPeers.length} Peer
          {qualifyingPeers.length === 1 ? "" : "s"} mit ähnlicher Marktkapitalisierung
          (Z-Score-Standardisierung, Median der Gruppe = Nullpunkt). Rein deskriptiv,
          keine Anlageempfehlung.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <QualityValueMatrix
          points={matrixPoints}
          valueLabel={own.fundamentals.evToEbitda !== undefined ? "EV/EBITDA" : "KGV"}
          qualityLabel={own.fundamentals.roic !== undefined ? "ROIC" : "Nettomarge"}
        />
        <PeerAxisBars data={axisRows} targetLabel={own.symbol} />
      </div>

      <div className="rounded-lg border border-hairline p-4">
        <div className="mb-3 text-sm font-medium text-ink-secondary">Target-Fit</div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(TARGET_FIT_PRESETS) as TargetFitKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setPreset(key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                preset === key
                  ? "bg-positive/10 text-positive ring-1 ring-positive/30"
                  : "bg-muted text-muted-foreground hover:bg-muted/70 hover:text-ink"
              }`}
            >
              {TARGET_FIT_PRESETS[key].label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={preset}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="mt-4 rounded-lg bg-muted/40 p-4"
          >
            {winnerMember && winner ? (
              <p className="text-sm text-ink-secondary">
                Basierend auf deinem Ziel &bdquo;{TARGET_FIT_PRESETS[preset].label}&ldquo; ist
                aktuell{" "}
                <span className="font-semibold text-positive">
                  {winnerMember.name} ({winnerMember.symbol})
                </span>{" "}
                der mathematische Sieger in dieser Peer-Group (Target-Fit:{" "}
                {winner.score.toFixed(0)} %).
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Für dieses Ziel liegen in der aktuellen Peer-Group nicht genug Daten vor.
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
